import { useEffect, useRef, useState } from 'react';
import { ExternalLink, Eye, Play, ShieldAlert, ShieldCheck, Wrench } from 'lucide-react';
import type {
  Resource,
  SecurityFinding,
  SecurityLiveTreatment,
  SecurityOverall,
  SecurityReport,
  SecuritySeverity,
  SecurityTreatment,
  SecurityTreatmentRun,
} from '@itr/shared';
import { formatRelative, packageAsFinding } from '@itr/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Badge, Drawer, useToast } from './ui';

interface SecurityState {
  platform: 'linux' | 'routeros' | null;
  running: boolean;
  report: SecurityReport | null;
  treatments: SecurityTreatmentRun[];
}

type Treat = (finding: SecurityFinding, treatment: SecurityTreatment) => void;

const POLL_RUNNING_MS = 4000;

export function severityIntent(severity: SecuritySeverity): 'bad' | 'warn' | 'info' | 'neutral' {
  if (severity === 'critical' || severity === 'high') return 'bad';
  if (severity === 'medium') return 'warn';
  if (severity === 'low') return 'info';
  return 'neutral';
}

export function OverallBadge({ overall }: { overall: SecurityOverall | null }) {
  if (!overall) return <Badge>not checked</Badge>;
  const label = { ok: 'no issues', warn: 'needs attention', critical: 'critical', error: 'check failed' }[overall];
  const intent = { ok: 'good', warn: 'warn', critical: 'bad', error: 'neutral' }[overall] as 'good' | 'warn' | 'bad' | 'neutral';
  return <Badge intent={intent}>{label}</Badge>;
}

/**
 * Known vulnerabilities, intrusion signs and security log of one resource —
 * see services/security-scan.ts. Hidden for resources the check cannot log
 * into.
 */
export function ResourceSecurityCard({ resource }: { resource: Resource }) {
  const { can } = useAuth();
  const toast = useToast();
  const [state, setState] = useState<SecurityState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [treating, setTreating] = useState<{ finding: SecurityFinding; treatment: SecurityTreatment } | null>(null);
  // Treatments run commands on the device: the same grants the server checks.
  const canTreat = can('tools:run') && can('resource:write');

  const load = async () => {
    try {
      setState(await api.get<SecurityState>(`/api/security/resource/${resource.id}`));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    setState(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource.id]);

  // Follow a running check until its report lands.
  useEffect(() => {
    if (!state?.running) return;
    const timer = setInterval(() => void load(), POLL_RUNNING_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.running, resource.id]);

  const scan = async () => {
    try {
      await api.post(`/api/security/resource/${resource.id}/scan`);
      setState((s) => (s ? { ...s, running: true } : s));
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'bad');
    }
  };

  if (!state || !state.platform) return null;
  const report = state.report;
  const Icon = report?.overall === 'ok' ? ShieldCheck : ShieldAlert;

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header">
        <Icon size={14} />
        <span className="card-title">Security</span>
        {report && (
          <span className="card-subtitle">
            {[report.os, `checked ${formatRelative(report.scannedAt)}`, `took ${Math.max(1, Math.round(report.durationMs / 1000))}s`]
              .filter(Boolean)
              .join(' · ')}
          </span>
        )}
        <span className="grow" />
        {!state.running && <OverallBadge overall={report?.overall ?? null} />}
        {state.running && (
          <span className="row subtle" style={{ gap: 6, fontSize: 12 }}>
            <span className="spinner" /> Checking…
          </span>
        )}
        {can('tools:run') && (
          <button className="btn btn-sm" style={{ marginLeft: 8 }} onClick={() => void scan()} disabled={state.running}>
            <Play size={12} /> Scan now
          </button>
        )}
      </div>

      <div className="card-body">
        {error && <div style={{ fontSize: 12.5, color: 'var(--itr-critical)' }}>{error}</div>}
        {!report && !state.running && (
          <div className="subtle" style={{ fontSize: 12.5 }}>
            Not checked yet. The check logs in over SSH, matches the installed {state.platform === 'routeros' ? 'RouterOS version' : 'packages'}{' '}
            against published vulnerabilities, and looks through accounts, keys, processes and logs for signs of intrusion.
          </div>
        )}
        {!report && state.running && (
          <div className="subtle" style={{ fontSize: 12.5 }}>
            The first check of a host can take a few minutes while vulnerability records are downloaded.
          </div>
        )}
        {report && (
          <SecurityReportBody
            report={report}
            onTreat={canTreat && !state.running ? (finding, treatment) => setTreating({ finding, treatment }) : undefined}
          />
        )}
        {(state.treatments ?? []).length > 0 && <TreatmentHistory runs={state.treatments} />}
      </div>

      <Drawer open={Boolean(treating)} onClose={() => { setTreating(null); void load(); }} title={treating?.treatment.label ?? ''}>
        {treating && (
          <TreatmentPanel
            resourceId={resource.id}
            finding={treating.finding}
            treatment={treating.treatment}
            onApplied={() => void load()}
          />
        )}
      </Drawer>
    </div>
  );
}

/**
 * One treatment, before and after: what it does and the exact commands, a
 * second confirmation when it interrupts service, then the device's output.
 */
function TreatmentPanel({
  resourceId,
  finding,
  treatment,
  onApplied,
}: {
  resourceId: string;
  finding: SecurityFinding;
  treatment: SecurityTreatment;
  onApplied: () => void;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [run, setRun] = useState<SecurityTreatmentRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState('');
  const liveRef = useRef<HTMLPreElement | null>(null);

  // While it runs, show the device's output as it arrives.
  useEffect(() => {
    if (!busy) return;
    let stopped = false;
    const tick = async () => {
      try {
        const l = await api.get<SecurityLiveTreatment>(`/api/security/resource/${resourceId}/treat/live`);
        if (!stopped && l.treatmentId === treatment.id) setLive(l.output);
      } catch {
        /* the final result comes with the apply call */
      }
    };
    void tick();
    const timer = setInterval(() => void tick(), 1500);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [busy, resourceId, treatment.id]);
  useEffect(() => {
    const el = liveRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [live]);

  const apply = async () => {
    setBusy(true);
    setError(null);
    setLive('');
    try {
      setRun(await api.post<SecurityTreatmentRun>(`/api/security/resource/${resourceId}/treat`, { findingId: finding.id, treatmentId: treatment.id }));
      onApplied();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const needsConfirm = treatment.disruptive && !confirmed;
  return (
    <div style={{ display: 'grid', gap: 12, fontSize: 13 }}>
      <div>
        <div className="subtle" style={{ fontSize: 12 }}>
          For
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
          <Badge intent={severityIntent(finding.severity)}>{finding.severity}</Badge>
          <span>{finding.title}</span>
        </div>
      </div>
      <div className="row" style={{ gap: 6 }}>
        <Badge intent={treatment.kind === 'inspect' ? 'info' : 'warn'}>{treatment.kind === 'inspect' ? 'inspect — changes nothing' : 'fix — changes the device'}</Badge>
        {treatment.disruptive && <Badge intent="bad">interrupts service</Badge>}
      </div>
      <div>{treatment.description}</div>
      <div>
        <div className="subtle" style={{ fontSize: 12, marginBottom: 4 }}>
          {treatment.kind === 'inspect' ? 'Runs' : 'Runs, as root / with full rights'}
        </div>
        <pre className="mono" style={{ fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, padding: 10, borderRadius: 6, background: 'var(--itr-surface-sunken)' }}>
          {treatment.commands.join('\n')}
        </pre>
        {treatment.confirmCommands && treatment.confirmCommands.length > 0 && (
          <>
            <div className="subtle" style={{ fontSize: 12, margin: '8px 0 4px' }}>
              Then, over a fresh connection — the device undoes the change by itself if this cannot run:
            </div>
            <pre className="mono" style={{ fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, padding: 10, borderRadius: 6, background: 'var(--itr-surface-sunken)' }}>
              {treatment.confirmCommands.join('\n')}
            </pre>
          </>
        )}
      </div>

      {!run && treatment.disruptive && (
        <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          I understand this interrupts service on this device.
        </label>
      )}
      {!run && (
        <div className="row" style={{ gap: 8 }}>
          <button className={`btn${treatment.kind === 'fix' ? ' btn-primary' : ''}`} disabled={busy || needsConfirm} onClick={() => void apply()}>
            {treatment.kind === 'inspect' ? <Eye size={14} /> : <Wrench size={14} />} {busy ? 'Running…' : treatment.kind === 'inspect' ? 'Run' : 'Apply'}
          </button>
          {busy && (
            <span className="subtle" style={{ fontSize: 12 }}>
              Installs can take several minutes. Keep this open.
            </span>
          )}
        </div>
      )}
      {busy && (
        <pre
          ref={liveRef}
          className="mono"
          style={{ fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, padding: 10, borderRadius: 6, background: 'var(--itr-surface-sunken)', maxHeight: 420, overflow: 'auto' }}
        >
          {live || 'Connecting…'}
        </pre>
      )}
      {error && <div style={{ color: 'var(--itr-critical)' }}>{error}</div>}
      {run && (
        <div>
          <div className="row" style={{ gap: 8, marginBottom: 6 }}>
            <Badge intent={run.ok ? 'good' : 'bad'}>{run.ok ? 'done' : 'failed'}</Badge>
            {run.ok && run.kind === 'fix' && (
              <span className="subtle" style={{ fontSize: 12 }}>
                A new check has started; the finding clears when it no longer applies.
              </span>
            )}
          </div>
          <pre className="mono" style={{ fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, padding: 10, borderRadius: 6, background: 'var(--itr-surface-sunken)', maxHeight: 420, overflow: 'auto' }}>
            {run.output}
          </pre>
        </div>
      )}
    </div>
  );
}

function TreatmentHistory({ runs }: { runs: SecurityTreatmentRun[] }) {
  return (
    <details style={{ marginTop: 10 }}>
      <summary style={{ fontSize: 13, cursor: 'pointer' }}>Treatments applied ({runs.length})</summary>
      <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
        {runs.map((r) => (
          <details key={r.id}>
            <summary className="row" style={{ gap: 8, fontSize: 12.5, cursor: 'pointer' }}>
              <Badge intent={r.ok ? 'good' : 'bad'}>{r.ok ? 'done' : 'failed'}</Badge>
              <span>{r.label}</span>
              <span className="subtle">
                {r.kind} · {r.userEmail ?? 'unknown'} · {formatRelative(r.appliedAt)}
              </span>
            </summary>
            <pre className="mono" style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: '6px 0 0', padding: 8, borderRadius: 6, background: 'var(--itr-surface-sunken)', maxHeight: 300, overflow: 'auto' }}>
              {r.output}
            </pre>
          </details>
        ))}
      </div>
    </details>
  );
}

function SecurityReportBody({ report, onTreat }: { report: SecurityReport; onTreat?: Treat }) {
  const v = report.vulnerabilities;
  const seriousFindings = report.findings.filter((f) => f.severity !== 'info').length;
  return (
    <>
      {report.error && (
        <div style={{ fontSize: 12.5, color: 'var(--itr-critical)', marginBottom: 10 }}>The check could not run: {report.error}</div>
      )}

      {!report.error && (
        <div className="stat-strip" style={{ marginBottom: 12 }}>
          <Stat
            label="Findings"
            value={String(seriousFindings)}
            tone={report.findings.some((f) => f.severity === 'critical') ? 'bad' : seriousFindings ? 'warn' : undefined}
          />
          <Stat
            label="Known vulnerabilities"
            value={v.source ? `${v.total}` : '—'}
            hint={v.source ? `${v.fixable} with a fix available` : (v.error ?? undefined)}
            tone={v.fixable ? 'warn' : undefined}
          />
          <Stat
            label={report.platform === 'routeros' ? 'Latest RouterOS' : 'Pending updates'}
            value={
              report.platform === 'routeros'
                ? (report.updates.latestVersion ?? '—')
                : report.updates.pending === null
                  ? '—'
                  : String(report.updates.pending)
            }
            hint={
              report.platform === 'routeros'
                ? report.updates.pending
                  ? 'upgrade available'
                  : 'up to date'
                : report.updates.security
                  ? `${report.updates.security} security${report.updates.rebootRequired ? ' · reboot needed' : ''}`
                  : report.updates.rebootRequired
                    ? 'reboot needed'
                    : undefined
            }
            tone={report.updates.security || report.updates.pending ? 'warn' : undefined}
          />
          <Stat
            label={`Failed logins ${report.logins.windowHours}h`}
            value={String(report.logins.failed)}
            hint={report.logins.topSources[0] ? `most from ${report.logins.topSources[0].source}` : undefined}
            tone={report.logins.failed >= 100 ? 'warn' : undefined}
          />
        </div>
      )}

      {report.findings.length > 0 && (
        <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
          {report.findings.map((f) => (
            <div key={f.id} className="row" style={{ gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
              <span style={{ minWidth: 64 }}>
                <Badge intent={severityIntent(f.severity)}>{f.severity}</Badge>
              </span>
              <div className="grow">
                <div>{f.title}</div>
                {f.detail && (
                  <div className="subtle" style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                    {f.detail}
                  </div>
                )}
                {onTreat && f.treatments && f.treatments.length > 0 && (
                  <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 5 }}>
                    {f.treatments.map((tr) => (
                      <button
                        key={tr.id}
                        className="btn btn-sm"
                        title={tr.description}
                        onClick={() => onTreat(f, tr)}
                        style={tr.disruptive ? { borderColor: 'var(--itr-warning)' } : undefined}
                      >
                        {tr.kind === 'inspect' ? <Eye size={12} /> : <Wrench size={12} />} {tr.label}
                      </button>
                    ))}
                  </div>
                )}
                {f.manual && (
                  <div className="subtle" style={{ fontSize: 12, marginTop: 4, fontStyle: 'italic' }}>
                    By hand: {f.manual}
                  </div>
                )}
              </div>
              <span className="subtle" style={{ fontSize: 11 }}>
                {f.category}
              </span>
            </div>
          ))}
        </div>
      )}
      {!report.error && report.findings.length === 0 && (
        <div className="subtle" style={{ fontSize: 12.5, marginBottom: 12 }}>
          No signs of intrusion and no risky settings found.
        </div>
      )}

      {v.error && v.source && <div className="subtle" style={{ fontSize: 12, marginBottom: 8 }}>{v.error}</div>}

      {v.packages.length > 0 && (
        <details style={{ marginBottom: 10 }}>
          <summary style={{ fontSize: 13, cursor: 'pointer' }}>
            Vulnerable {report.platform === 'routeros' ? 'software' : 'packages'} ({v.packages.length}
            {v.packages.length >= 150 ? '+' : ''}) · source {v.source}
          </summary>
          <div className="table-wrap" style={{ maxHeight: 520, marginTop: 8 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Package</th>
                  <th style={{ width: 200 }}>Installed</th>
                  <th style={{ width: 110 }}>Worst</th>
                  <th data-align="right" style={{ width: 90 }}>
                    Fixable
                  </th>
                  <th data-align="right" style={{ width: 80 }}>
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {v.packages.map((p) => (
                  <PackageRow key={`${p.name}@${p.version}`} pkg={p} onTreat={onTreat} />
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {(report.logins.topSources.length > 0 || report.logins.successes.length > 0) && (
        <details style={{ marginBottom: 10 }}>
          <summary style={{ fontSize: 13, cursor: 'pointer' }}>
            Logins — {report.logins.failed} failed in {report.logins.windowHours}h, {report.logins.successes.length} successful in 7 days
          </summary>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, marginTop: 8 }}>
            {report.logins.topSources.length > 0 && (
              <CountList title="Failed, by source" rows={report.logins.topSources} />
            )}
            {report.logins.topUsers.length > 0 && <CountList title="Failed, by user name" rows={report.logins.topUsers} />}
          </div>
          {report.logins.successes.length > 0 && (
            <div className="table-wrap" style={{ marginTop: 10 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>From</th>
                    <th>Method</th>
                    <th data-align="right">Times</th>
                    <th>Last</th>
                  </tr>
                </thead>
                <tbody>
                  {report.logins.successes.map((s) => (
                    <tr key={`${s.user}|${s.source}|${s.method}`}>
                      <td>{s.user}</td>
                      <td className="mono">{s.source}</td>
                      <td>{s.method}</td>
                      <td data-align="right" className="nums">
                        {s.count}
                      </td>
                      <td className="subtle">{s.ts ? formatRelative(s.ts) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </details>
      )}

      {report.logs.length > 0 && (
        <details>
          <summary style={{ fontSize: 13, cursor: 'pointer' }}>Security log ({report.logs.length} most recent)</summary>
          <div className="mono" style={{ fontSize: 11.5, maxHeight: 360, overflow: 'auto', marginTop: 8, display: 'grid', gap: 2 }}>
            {report.logs.map((e, i) => (
              <div key={i} className="row" style={{ gap: 8, alignItems: 'baseline' }}>
                <span className="subtle" style={{ minWidth: 110 }}>
                  {e.ts ? new Date(e.ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                </span>
                <span style={{ minWidth: 86 }}>
                  <Badge intent={e.kind === 'login-failed' ? 'warn' : e.kind === 'login-ok' ? 'good' : e.kind === 'account' ? 'info' : 'neutral'}>
                    {e.kind}
                  </Badge>
                </span>
                <span style={{ wordBreak: 'break-all' }}>{e.text}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </>
  );
}

function Stat({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: 'bad' | 'warn' }) {
  return (
    <div className="stat-chip">
      <div className="stat-chip-label">{label}</div>
      <div
        className="stat-chip-value"
        style={{ color: tone === 'bad' ? 'var(--itr-critical)' : tone === 'warn' ? 'var(--itr-warning)' : undefined }}
      >
        {value}
      </div>
      {hint && (
        <div className="subtle" style={{ fontSize: 11.5 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function CountList({ title, rows }: { title: string; rows: Array<{ source: string; count: number }> }) {
  return (
    <div>
      <div className="subtle" style={{ fontSize: 12, marginBottom: 4 }}>
        {title}
      </div>
      {rows.map((r) => (
        <div key={r.source} className="row" style={{ fontSize: 12.5, gap: 8 }}>
          <span className="mono grow truncate">{r.source}</span>
          <span className="nums">{r.count}</span>
        </div>
      ))}
    </div>
  );
}

function PackageRow({ pkg, onTreat }: { pkg: SecurityReport['vulnerabilities']['packages'][number]; onTreat?: Treat }) {
  const [open, setOpen] = useState(false);
  const fixable = pkg.vulns.filter((x) => x.fixedVersion).length;
  const asFinding = packageAsFinding(pkg);
  const worst = asFinding.severity;
  return (
    <>
      <tr onClick={() => setOpen(!open)} style={{ cursor: 'pointer' }}>
        <td>
          <span className="subtle">{open ? '▾' : '▸'}</span> {pkg.name}
        </td>
        <td className="mono" style={{ fontSize: 11.5 }}>
          {pkg.version}
        </td>
        <td>
          <Badge intent={severityIntent(worst)}>{worst}</Badge>
        </td>
        <td data-align="right" className="nums">
          {fixable}
        </td>
        <td data-align="right" className="nums">
          {pkg.totalVulns ?? pkg.vulns.length}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} style={{ background: 'var(--itr-surface-2, transparent)' }}>
            <div style={{ display: 'grid', gap: 6, fontSize: 12 }}>
              {onTreat && pkg.treatments && pkg.treatments.length > 0 && (
                <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {pkg.treatments.map((tr) => (
                    <button
                      key={tr.id}
                      className="btn btn-sm"
                      title={tr.description}
                      onClick={(e) => {
                        e.stopPropagation();
                        onTreat(asFinding, tr);
                      }}
                      style={tr.disruptive ? { borderColor: 'var(--itr-warning)' } : undefined}
                    >
                      {tr.kind === 'inspect' ? <Eye size={12} /> : <Wrench size={12} />} {tr.label}
                    </button>
                  ))}
                </div>
              )}
              {pkg.manual && (
                <div className="subtle" style={{ fontStyle: 'italic' }}>
                  By hand: {pkg.manual}
                </div>
              )}
              {pkg.vulns.map((x) => (
                <div key={x.id} className="row" style={{ gap: 8, alignItems: 'baseline' }}>
                  <a href={x.url} target="_blank" rel="noreferrer noopener" className="mono" style={{ minWidth: 170 }}>
                    {x.aliases[0] ?? x.id} <ExternalLink size={10} />
                  </a>
                  <Badge intent={severityIntent(x.severity)}>{x.severity}</Badge>
                  <span className="subtle" style={{ minWidth: 150 }}>
                    {x.fixedVersion ? `fixed in ${x.fixedVersion}` : 'no fix yet'}
                  </span>
                  <span className="grow">{x.summary}</span>
                </div>
              ))}
              {(pkg.totalVulns ?? 0) > pkg.vulns.length && (
                <div className="subtle">…and {(pkg.totalVulns ?? 0) - pkg.vulns.length} more without a fix.</div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
